import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Drawer, Empty, Layout, Popconfirm, Space, Spin, Table, Tag, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getApiErrorMessage, serverFetch } from '@api/server';
import { getAgents } from '@components/agents';

const { Content } = Layout;
const { Text, Title } = Typography;

export const AGENT_TOOL_TYPE_LABELS = {
  1: 'File search',
  2: 'MCP',
  3: 'Пространство (в External ID указать #)',
  4: 'Внешняя RAG',
};

export const getAgentToolTypeLabel = (vid) => AGENT_TOOL_TYPE_LABELS[vid] || `Инструмент ${vid}`;

export const getAgentTools = () => serverFetch('agenttool');

export const getAgentTool = (id) => serverFetch(`agenttool/${id}`);

export const createAgentTool = (data) => serverFetch('agenttool', {
  method: 'POST',
  bodyData: data,
});

export const updateAgentTool = (data) => serverFetch('agenttool', {
  method: 'PUT',
  bodyData: data,
});

export const deleteAgentTool = (id) => serverFetch(`agenttool/${id}`, {
  method: 'DELETE',
});

const AgentTools = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { agentId } = useParams();
  const [agentTools, setAgentTools] = useState([]);
  const [agents, setAgents] = useState([]);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [deletingAgentToolId, setDeletingAgentToolId] = useState(null);
  const [deleteConfirmAgentToolId, setDeleteConfirmAgentToolId] = useState(null);
  const scopedAgentId = useMemo(() => {
    if (!agentId) {
      return null;
    }

    const nextAgentId = Number(agentId);
    return Number.isInteger(nextAgentId) && nextAgentId > 0 ? nextAgentId : null;
  }, [agentId]);
  const isScoped = Boolean(agentId);
  const refreshKey = location.state?.agentToolsRefreshKey;

  const agentNameById = useMemo(() => agents.reduce((result, agent) => ({
    ...result,
    [agent.id]: agent.name,
  }), {}), [agents]);
  const currentAgent = useMemo(() => (
    scopedAgentId ? agents.find((agent) => agent.id === scopedAgentId) : null
  ), [agents, scopedAgentId]);
  const closeDrawer = useCallback(() => {
    navigate('/agents');
  }, [navigate]);
  const getCreatePath = useCallback(() => (
    isScoped ? `/agents/${scopedAgentId}/tools/new` : '/agent-tools/new'
  ), [isScoped, scopedAgentId]);
  const getEditPath = useCallback((toolId) => (
    isScoped ? `/agents/${scopedAgentId}/tools/${toolId}` : `/agent-tools/${toolId}`
  ), [isScoped, scopedAgentId]);

  const loadAgentTools = useCallback(async () => {
    setError('');
    setIsLoading(true);
    try {
      const [toolsResult, agentsResult] = await Promise.all([
        getAgentTools(),
        getAgents(),
      ]);
      setAgents(agentsResult || []);
      setAgentTools((toolsResult || []).filter((tool) => (
        scopedAgentId ? Number(tool.agentid) === scopedAgentId : true
      )));
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setIsLoading(false);
    }
  }, [scopedAgentId]);

  useEffect(() => {
    loadAgentTools();
  }, [loadAgentTools, refreshKey]);

  const handleDeleteAgentTool = async (agentToolId) => {
    setError('');
    setDeletingAgentToolId(agentToolId);
    try {
      await deleteAgentTool(agentToolId);
      setAgentTools((currentAgentTools) => (
        currentAgentTools.filter((agentTool) => agentTool.id !== agentToolId)
      ));
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setDeletingAgentToolId(null);
      setDeleteConfirmAgentToolId(null);
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 90,
    },
    {
      title: 'Название',
      dataIndex: 'name',
      render: (name) => <Text strong>{name}</Text>,
    },
    !isScoped ? {
      title: 'Агент',
      dataIndex: 'agentid',
      render: (agentid) => agentNameById[agentid] || `#${agentid}`,
    } : null,
    {
      title: 'Тип',
      dataIndex: 'vid',
      width: 180,
      render: (vid) => <Tag>{getAgentToolTypeLabel(vid)}</Tag>,
    },
    {
      title: 'Активен',
      dataIndex: 'active',
      width: 120,
      render: (active) => (
        <Tag color={active ? 'green' : 'red'}>
          {active ? 'Активен' : 'Отключен'}
        </Tag>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      render: (_, agentTool) => (
        <span
          className={
            deleteConfirmAgentToolId === agentTool.id || deletingAgentToolId === agentTool.id
              ? 'table-row-action table-row-action-visible'
              : 'table-row-action'
          }
          onClick={(event) => event.stopPropagation()}
        >
          <Popconfirm
            title="Удалить инструмент?"
            description="Инструмент будет удален из агента."
            okText="Удалить"
            cancelText="Отмена"
            open={deleteConfirmAgentToolId === agentTool.id}
            okButtonProps={{ danger: true, loading: deletingAgentToolId === agentTool.id }}
            onOpenChange={(open) => setDeleteConfirmAgentToolId(open ? agentTool.id : null)}
            onConfirm={() => handleDeleteAgentTool(agentTool.id)}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              aria-label="Удалить"
              loading={deletingAgentToolId === agentTool.id}
            />
          </Popconfirm>
        </span>
      ),
    },
  ].filter(Boolean);

  const content = (
    <>
      <div className="panel-title users-title">
        <Space direction="vertical" size={0}>
          <Title level={5}>
            {isScoped ? `Инструменты: ${currentAgent?.name || `агент #${scopedAgentId || ''}`}` : 'Инструменты агентов'}
          </Title>
          <Text type="secondary">{agentTools.length}</Text>
        </Space>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={loadAgentTools}>
            Обновить
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate(getCreatePath())}>
            Создать
          </Button>
        </Space>
      </div>

      {error && (
        <Alert className="panel-alert" type="error" message={error} showIcon />
      )}

      <Spin spinning={isLoading}>
        <Table
          className="agent-tools-table"
          rowKey="id"
          columns={columns}
          dataSource={agentTools}
          locale={{ emptyText: <Empty description="Инструментов пока нет" /> }}
          pagination={false}
          onRow={(agentTool) => ({
            onClick: () => navigate(getEditPath(agentTool.id)),
          })}
        />
      </Spin>
    </>
  );

  if (isScoped) {
    return (
      <Drawer
        className="user-drawer agent-tools-drawer"
        title={currentAgent?.name ? `Инструменты агента: ${currentAgent.name}` : 'Инструменты агента'}
        placement="right"
        width={760}
        open
        onClose={closeDrawer}
        destroyOnHidden
        footer={(
          <div className="user-drawer-footer">
            <Button onClick={closeDrawer}>Закрыть</Button>
          </div>
        )}
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Content className="users-page">
      <div className="users-panel">
        {content}
      </div>
    </Content>
  );
};

export default AgentTools;
